import { ColorEnum } from '@/constants/color'
import { PaymentType } from '@/features/graphql/generated/graphql'
import { Layout } from '@/features/layout/types'
import AmountInput from '@/features/shiftCloseouts/components/AmountInput'
import DifferenceBadge from '@/features/shiftCloseouts/components/ShiftCloseoutForm/parts/PaymentTotals/parts/PaymentTotalRow/parts/DifferenceBadge'
import ManualAmount from '@/features/shiftCloseouts/components/ShiftCloseoutForm/parts/PaymentTotals/parts/PaymentTotalRow/parts/ManualAmount'
import PaymentMethodInfo from '@/features/shiftCloseouts/components/ShiftCloseoutForm/parts/PaymentTotals/parts/PaymentTotalRow/parts/PaymentMethodInfo'
import { PaymentDifference, ShiftCloseoutPaymentMethod } from '@/features/shiftCloseouts/types'
import CardFilledIcon from '@/features/ui/icons/components/CardFilledIcon'
import CardOnlineIcon from '@/features/ui/icons/components/CardOnlineIcon'
import CashIcon from '@/features/ui/icons/components/CashIcon'
import MealVoucherCardIcon from '@/features/ui/icons/components/MealVoucherCardIcon'
import MealVoucherIcon from '@/features/ui/icons/components/MealVoucherIcon'
import ReceiptIcon from '@/features/ui/icons/components/ReceiptIcon'
import { TFunction } from 'i18next'
import { ComponentType, FC, ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'
import { SvgProps } from 'react-native-svg'
import { createStyleSheet, useStyles } from 'react-native-unistyles'

interface PaymentTotalRowProps {
    difference?: PaymentDifference
    layout: Layout
    paymentMethod: ShiftCloseoutPaymentMethod
}

const getPaymentTypeConfig = (
    t: TFunction<'app'>
): Record<PaymentType, { Icon: ComponentType<SvgProps>; label: string }> => ({
    [PaymentType.Cash]: {
        Icon: CashIcon,
        label: t('shiftCloseouts.paymentMethods.paymentType.Cash'),
    },
    [PaymentType.CardOnsite]: {
        Icon: CardFilledIcon,
        label: t('shiftCloseouts.paymentMethods.paymentType.CardOnsite'),
    },
    [PaymentType.Online]: {
        Icon: CardOnlineIcon,
        label: t('shiftCloseouts.paymentMethods.paymentType.Online'),
    },
    [PaymentType.MealVoucher]: {
        Icon: MealVoucherIcon,
        label: t('shiftCloseouts.paymentMethods.paymentType.MealVoucher'),
    },
    [PaymentType.MealVoucherCard]: {
        Icon: MealVoucherCardIcon,
        label: t('shiftCloseouts.paymentMethods.paymentType.MealVoucherCard'),
    },
    [PaymentType.Invoice]: {
        Icon: ReceiptIcon,
        label: t('shiftCloseouts.paymentMethods.paymentType.Invoice'),
    },
})

const PaymentTotalRow: FC<PaymentTotalRowProps> = ({ difference, layout, paymentMethod }) => {
    const { t } = useTranslation()
    const { styles } = useStyles(stylesheet, { layout })
    const compact = layout === 'compact'
    const resultFieldStyle = compact ? styles.compactResultField : undefined
    const paymentType = paymentMethod.paymentType
    const paymentTypeConfig = paymentType ? getPaymentTypeConfig(t)[paymentType] : undefined

    const label = paymentTypeConfig?.label ?? paymentMethod.identifier
    const automatic = paymentMethod.mode === 'automatic' || !paymentType
    const accessibilityLabel = t('shiftCloseouts.paymentMethods.actualAmount', {
        paymentMethod: label,
    })
    const renderResultField = (field: ReactElement, testID: string) =>
        compact ? (
            <View style={styles.compactResultColumn} testID={testID}>
                {field}
            </View>
        ) : (
            field
        )

    return (
        <View style={styles.container}>
            <PaymentMethodInfo
                automatic={automatic}
                expectedAmount={paymentMethod.expectedAmount}
                Icon={paymentTypeConfig?.Icon}
                label={label}
            />

            <View
                style={styles.result}
                testID={`shift-closeout-payment-result-${paymentMethod.identifier}`}
            >
                {(!automatic || compact) &&
                    renderResultField(
                        <DifferenceBadge
                            difference={difference}
                            paymentMethodLabel={label}
                            placeholder={automatic}
                            style={resultFieldStyle}
                        />,
                        `shift-closeout-payment-difference-column-${paymentMethod.identifier}`
                    )}
                {automatic
                    ? renderResultField(
                          <AmountInput
                              accessibilityLabel={accessibilityLabel}
                              currency={paymentMethod.expectedAmount.currency}
                              disabled
                              style={resultFieldStyle}
                              testID={`shift-closeout-actual-${paymentMethod.identifier}`}
                              value={paymentMethod.expectedAmount.amount}
                          />,
                          `shift-closeout-payment-actual-column-${paymentMethod.identifier}`
                      )
                    : paymentType
                      ? renderResultField(
                            <ManualAmount
                                accessibilityLabel={accessibilityLabel}
                                currency={paymentMethod.expectedAmount.currency}
                                paymentType={paymentType}
                                style={resultFieldStyle}
                            />,
                            `shift-closeout-payment-actual-column-${paymentMethod.identifier}`
                        )
                      : null}
            </View>
        </View>
    )
}

const stylesheet = createStyleSheet({
    container: {
        gap: 16,
        padding: 8,
        borderColor: ColorEnum.GRAY_200,
        borderRadius: 12,
        borderWidth: 1,
        backgroundColor: ColorEnum.WHITE,
        variants: {
            layout: {
                compact: {
                    alignItems: 'stretch',
                },
                extended: {
                    flexDirection: 'row',
                    alignItems: 'center',
                },
            },
        },
    },
    result: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 8,
        variants: {
            layout: {
                compact: {
                    alignSelf: 'stretch',
                    width: '100%',
                },
                extended: {
                    flexShrink: 0,
                },
            },
        },
    },
    compactResultColumn: {
        flexBasis: 0,
        flexGrow: 1,
        flexShrink: 1,
        minWidth: 0,
        width: 0,
    },
    compactResultField: {
        width: '100%',
    },
})

export default PaymentTotalRow
